/**
 * ShareModal - Share flower / illustration via link+password OR email
 * Matches Curex 10_cong_cu/chia_se_giai_phap_bong_hoa.png
 */
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Platform,
} from 'react-native';
import { Icon } from './Icon';
import { Toast, useToast } from './Toast';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export interface ShareModalProps {
  visible: boolean;
  onClose: () => void;
  /** Full base64 share URL (data param) */
  shareUrl: string;
  /** Customer name for header */
  customerName?: string;
  /** Optional callback after send-email action */
  onSendEmail?: (email: string, message: string) => Promise<boolean>;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  visible,
  onClose,
  shareUrl,
  customerName,
  onSendEmail,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { toastProps, showToast, hideToast } = useToast();

  if (!visible) return null;

  // Generate a 6-char password
  const password = 'XUM' + Math.random().toString(36).slice(2, 8).toUpperCase();

  const copyToClipboard = (text: string, label: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      showToast(`${label} đã được sao chép vào bộ nhớ tạm`, 'success');
    } else {
      showToast(`${label}: ${text}`, 'info');
    }
  };

  const handleSendEmail = async () => {
    if (!email || !email.includes('@')) {
      showToast('Vui lòng nhập email hợp lệ', 'error');
      return;
    }
    if (onSendEmail) {
      setSending(true);
      try {
        const ok = await onSendEmail(email, message);
        if (ok) {
          showToast(`Đã gửi minh họa tới ${email}`, 'success');
          setEmail('');
          setMessage('');
          onClose();
        } else {
          showToast('Không thể gửi email. Vui lòng thử lại', 'error');
        }
      } finally {
        setSending(false);
      }
    } else {
      showToast(`Đã gửi minh họa tới ${email} (Demo)`, 'success');
      setEmail('');
      setMessage('');
      onClose();
    }
  };

  return (
    <View style={styles.backdrop}>
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Chia sẻ Bông hoa giải pháp</Text>
          <TouchableOpacity onPress={onClose}>
            <Icon name="close" size={22} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        <View style={styles.body}>
          <View style={styles.infoBanner}>
            <Text style={styles.infoBannerText}>
              Bạn có thể sao chép link & mật khẩu để gửi thủ công, hoặc gửi trực tiếp qua email.
            </Text>
          </View>

          {/* Link */}
          <Text style={styles.label}>Link Bông hoa giải pháp</Text>
          <View style={styles.linkRow}>
            <View style={styles.linkBox}>
              <Icon name="send" size={16} color={colors.onSurfaceVariant} />
              <Text style={styles.linkText} numberOfLines={1}>{shareUrl}</Text>
            </View>
            <TouchableOpacity
              style={styles.copyIconBtn}
              onPress={() => copyToClipboard(shareUrl, 'Link')}
            >
              <Icon name="description" size={18} color={colors.primary} />
            </TouchableOpacity>
          </View>

          {/* Password */}
          <Text style={styles.label}>Mật khẩu truy cập</Text>
          <View style={styles.linkRow}>
            <View style={styles.linkBox}>
              <Icon name="lock" size={16} color={colors.onSurfaceVariant} />
              <Text style={styles.linkText}>{showPassword ? password : '••••••••'}</Text>
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)} style={styles.eyeBtn}>
                <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={18} color={colors.onSurfaceVariant} />
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              style={styles.copyIconBtn}
              onPress={() => copyToClipboard(password, 'Mật khẩu')}
            >
              <Icon name="description" size={18} color={colors.primary} />
            </TouchableOpacity>
          </View>

          {/* Divider */}
          <View style={styles.divider}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>Hoặc</Text>
            <View style={styles.dividerLine} />
          </View>

          {/* Email form */}
          <Text style={styles.label}>Gửi qua email</Text>
          <TextInput
            style={styles.input}
            value={email}
            onChangeText={setEmail}
            placeholder="Nhập email..."
            placeholderTextColor={colors.onSurfaceVariant}
            keyboardType="email-address"
            autoCapitalize="none"
          />

          <Text style={styles.label}>Tin nhắn kèm theo (tùy chọn)</Text>
          <TextInput
            style={[styles.input, styles.textarea]}
            value={message}
            onChangeText={setMessage}
            placeholder="Nhập tin nhắn..."
            placeholderTextColor={colors.onSurfaceVariant}
            multiline
            numberOfLines={3}
          />
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
            <Text style={styles.cancelBtnText}>Đóng</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.submitBtn, !email.includes('@') && styles.submitBtnDisabled]}
            onPress={handleSendEmail}
            disabled={sending || !email.includes('@')}
          >
            <Text style={styles.submitBtnText}>{sending ? 'Đang gửi...' : 'Gửi chia sẻ'}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Toast — thay Alert.alert (no-op trên web) */}
      <Toast
        visible={toastProps.visible}
        message={toastProps.message}
        variant={toastProps.variant}
        onHide={hideToast}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 1000,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    width: '100%',
    maxWidth: 520,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
  },
  title: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  body: {
    padding: spacing[4],
    gap: spacing[2],
  },
  infoBanner: {
    backgroundColor: '#f0fdf4',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    marginBottom: spacing[2],
  },
  infoBannerText: {
    fontSize: 12,
    color: '#166534',
    lineHeight: 18,
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
    marginTop: spacing[1],
  },
  linkRow: {
    flexDirection: 'row',
    gap: spacing[2],
    alignItems: 'center',
  },
  linkBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[2.5],
    height: 38,
  },
  linkText: {
    flex: 1,
    fontSize: 13,
    color: colors.onSurface,
  },
  eyeBtn: {
    padding: 4,
  },
  copyIconBtn: {
    width: 38,
    height: 38,
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing[3],
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.outline,
  },
  dividerText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginHorizontal: spacing[3],
  },
  input: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[2.5],
    paddingVertical: spacing[1.5],
    fontSize: 14,
    color: colors.onSurface,
    minHeight: 38,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  textarea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing[2],
    padding: spacing[3],
    borderTopWidth: 1,
    borderTopColor: colors.outlineVariant,
  },
  cancelBtn: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    borderRadius: 999,
    backgroundColor: '#f1f5f9',
  },
  cancelBtnText: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.semibold,
  },
  submitBtn: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    borderRadius: 999,
    backgroundColor: colors.onSurfaceVariant,
  },
  submitBtnDisabled: {
    backgroundColor: '#cbd5e1',
  },
  submitBtnText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: fontWeights.bold,
  },
});